import React from "react";
import Autocomplete from "react-autocomplete";
import Select from 'react-select';
import availableTags from "./available_tags.js";
import { shortDollarFormat, numberWithCommas, trackClick, getUrlParams2 } from "../util/util.js"
import Toggle from '../util/toggle.js'
import {Heart, Close, DropdownCaret, MagnifyingGlass, ArrowDown, ArrowUp } from '../util/icons_svg.js'
import './search_form.scss';

const defaults = require("../defaults.json");

const selOptionStyle = {
    cursor: "default",
    display: "block",
    fontSize: "inherit",
    padding: "8px 12px",
    width: "100%",
    userSelect: "none",
    boxSizing: "border-box"
};

const focusedStyle = {
    backgroundColor: "#DEEBFF",
    color: "inherit"
};

const PRICES = [
    ...Array.from({length:10}, (v, k) => (k * 20 + 100)),
    ...Array.from({length:10}, (v, k) => k * 50 + 300),
    ...Array.from({length:2}, (v, k) => k * 100 + 800),
    ...Array.from({length:10}, (v, k) => (k * 200 + 1000)),
    ...Array.from({length:10}, (v, k) => k * 500 + 3000),
    ...Array.from({length:3}, (v, k) => k * 1000 + 8000),
].map(item => item * 1000);

const RENTS = [
    ...Array.from({length:16}, (v, k) => (k * 100 + 400)),
    ...Array.from({length:16}, (v, k) => (k * 200 + 2000)),
];

const SQUAREFEET = [
    ...Array.from({length:4}, (v, k) => (k * 200 + 200)),
    ...Array.from({length:10}, (v, k) => (k * 500 + 500)),
];

const RESALE_FEATURES = [
    {name: "open_house", label: "Open House"},
    {name: "just_listed", label: "Just Listed"},
    {name: "new_construction", label: "New Construction"},
    {name: "foreclosure", label: "Foreclosure"},
    {name: "waterfront", label: "Waterfront"},
    {name: "heating_cooling", label: "Central Air"},
    {name: "basement", label: "Basement"},
    {name: "fireplace", label: "Fireplace"},
    {name: "parkinggarage", label: "Parking/Garage"},
    {name: "pool", label: "Pool"},
    {name: "golf_course", label: "Golf Course"}
];

const RENT_FEATURES = [
    {name: "pets_exists", label: "Pets Allowed"},
    {name: "waterfront", label: "Waterfront"},
    {name: "parking_exists", label: "Parking"},
    {name: "utilities_included", label: "Utilities Included"},
    {name: "air_conditioning_exists", label: "Air Conditioning"},
    {name: "affordable_housing", label: "Affordable Housing"},
    {name: "furnished", label: "Furnished"}
];

const PROPERTY_TYPES = [
    "Apartment",
    "Condo",
    "Farm",
    "Lots and Land",
    "Manufactured Home",
    "Mobile Home",
    "Multi-Family",
    "Other",
    "Senior Community",
    "Single Family",
    "Studio",
    "Timeshare",
    "Townhouse",
    "Vacation/Resort"
];

const isZip = (addressObj) => {
    return /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(addressObj);
};

const parseCityState = (cityState) => {
    return cityState
        .replace(/\+/g, ' ')
        .replace('_', ', ')
};

const SORT_OPTION_MAP = {
    'showcase' : "Featured",
    'favorite' : "Favorites",
    'price:d': "Price-Highest",
    'price': "Price-Lowest",
    'date_created:d': "Listing-Newest",
    'number_sqfeet:d': "Largest-SqFt"
}

const SORT_OPTIONS = {
    'resale': [
        'showcase',
        'price:d',
        'price',
        'date_created:d',
        'number_sqfeet:d'
    ],
    'rentals': [
        'showcase',
        'price:d',
        'price',
        'date_created:d'
    ]
}

const SORT_OPTION_Display = {
    'showcase' : (<div className="noIcon"><span className="text">Featured</span></div>),
    'favorite' : (<div className="noIcon"><span className="text">Favorites</span></div>),
    'price:d': (<><span className="text">Price</span><span className="icon"><ArrowDown/></span></>),
    'price': (<><span className="text">Price</span><span className="icon"><ArrowUp/></span></>),
    'date_created:d': (<><span className="text">Newest</span><span className="icon"><ArrowDown/></span></>),
    'number_sqfeet:d':  (<><span className="text">SqFt</span><span className="icon"><ArrowDown/></span></>),
}

const LIST_TYPE_MAP = {
    'resale': 'For Sale',
    'rentals': 'For Rent',
    'favorites': 'Favorites',
    'list_property': 'List a Property',
    'open_house': "Open House"
}

const LIST_TYPES = [
    'resale',
    'rentals',
    'open_house'
].map(item => ({value: item, label: LIST_TYPE_MAP[item]}));

const FAV_MAP = {
    'search': (<span><Heart filled={false}/> Search all properties </span>),
    'fav_fromlist': (<span><Heart filled={true}/> For Sale saved </span>),
    'fav_rentals': (<span><Heart filled={true}/> Rentals saved</span>),
    'fav_all': (<span><Heart filled={true}/> View my saved</span>)
}

const favMapForButton = (key) => (
    key === "search"
    ? <span><Heart /> Search all</span>
    : <span><Heart filled={true}/> My Saved</span>
)

const FAV_TYPES = [
    'search',
    'fav_all'
].map(item => ({value: item, label: FAV_MAP[item]}));


const getSortOptions = (listType) => {
    // create the list of value/label objects needed by the Select component
    listType = listType || "resale";
    listType = listType === "open_house" ? "resale" : listType;
    return [{value: "Sort by", label: "Sort by...", isDisabled: true},
        ...SORT_OPTIONS[listType].map((item) => ({value: item, label: SORT_OPTION_MAP[item]}))];
}

const propTypeString = window.innerWidth > 500 ? "Property Type": "Prop. Type"

function parseRange(urlRange) {
    // price=min-max  or na-max or min-na
    urlRange = /(.*)-(.*)/.exec(urlRange);
    return urlRange !== null
        ? urlRange.slice(1).map(item => item === "na" ? null : item)
        : [null, null];
};

function parseFeatures(urlFeatures) {
    // waterfront,heating_cool
    return urlFeatures.split(',').map((feature) => feature.replace(/\+/g, ' '));
}

function getLocationDisplay(address, address_distance, show_radius, emptySearchText, geo) {
    const zipDisplay = isZip(address) ? "Zipcode: " : "";
    // Do not display distance if address is emptySearchText or is an ad id
    const distanceDisplay = (!geo) ? ''
                                   : (address_distance && show_radius && address !== emptySearchText && !address.includes('adid:'))
                                        ? ` (${address_distance} miles)`
                                        : "";
    const addressDisplay = address.includes('adid:') ? 'Property ID: ' + address.split(':')[1] : address;

    return address
        ? `${zipDisplay}${addressDisplay}${distanceDisplay}`
        : emptySearchText;
}

const rangeParam = (rangeObj) => {
    let urlRangeMin = rangeObj.min || null;
    let urlRangeMax = rangeObj.max || null;
    let urlRange = urlRangeMin && urlRangeMax ? urlRangeMin + '-' + urlRangeMax :
                                                urlRangeMin ? urlRangeMin + '-na' : 'na-' + urlRangeMax;
    return urlRangeMin || urlRangeMax ?
        rangeObj.metric + '=' + urlRange : null;
}

const listParam = (listName, list) => (
    // e.g. ["waterfront" "heating_cooling"]
    list.length && listName + '=' + list.map((item)=>item.replace(/ /g, '+')).join(",")
);

const basicParam = (name, value) => (value ? `${name}=${value}` : null);

const booleanParam = (name, value) => (value ? `${name}=true` : null)


function getUrlString(state, emptyLocationString, ad_number) {
    // for later: pass in state, return the query param string
    // address - append 'adid:' to ad_number for future conditional logic to properly display
    let urlStringArray = [
        `address=${state.address
            ? state.address === emptyLocationString
                ? ''
                : ad_number
                    ? `adid:${ad_number}`
                    : state.address.replace(', ', '_').replace(/ /g, '+')
            : ''}`,
        basicParam('list_type', state.listType),
        basicParam('show_radius', state.showRadius),
        basicParam('address_distance', state.address_distance),
        rangeParam(state.listType === "rentals" ? state.priceRent : state.priceSale),
        rangeParam(state.beds),
        rangeParam(state.baths),
        rangeParam(state.acres),
        rangeParam(state.sqft),
        listParam('features', state.listType === "rentals"
            ? state.featuresRental.selected
            : state.featuresSale.selected),
        listParam('property_types', state.properties.selected),
        basicParam('sortby', state.sortby),
        basicParam('saved', state.saved),
        booleanParam('showcase', state.showcase),
        basicParam('detail_page', state.detailItemKey)
    ];

    const urlString = urlStringArray.filter(v => v).join("&")
    return urlString
}

function selectMapping(optionList) {
 return optionList.reduce((accumulator, item) => {
        accumulator[item.value] = item.label;
        return accumulator;
    }, {})
}

function getGTMTags(urlString) {
    const urlArray = urlString.split('&');
    const urlPair = urlArray.map((item => {
        const objKey = item.split('=')[0];
        const objValue = item.split('=')[1];
        return { [objKey]: objValue };
    }))
    const GTMTags = Object.assign({}, ...urlPair);
    return GTMTags;
}
class RangeSearch extends React.Component {
    render() {
        const {minRange, maxRange, minLabel, maxLabel, rangeObj, updateParams } = this.props;
        return (
            <>
                {['min', 'max'].map((minMax, index) => {
                    let range = minMax === 'min' ? minRange : maxRange;
                    return (
                         <div key={index}>
                            <label>{minMax === 'min' ? minLabel : maxLabel}</label>
                            <Select
                                className={rangeObj.metric + '_min'}
                                classNamePrefix="sel"
                                width={rangeObj.metric.includes('price') ? '100px' : '0'}
                                name={minMax === 'min' ? rangeObj.metric + '_min' : rangeObj.metric + '_max'}
                                placeholder="None"
                                isSearchable={window.innerWidth > 768}
                                options={
                                    [{value: "", label: "None"},
                                    ...range.map((item) => {
                                        return rangeObj.metric.includes('price') ?
                                            {value: item, label: numberWithCommas(item)} : {value: item, label: String(item)}
                                        })
                                ]}
                                getItemValue={(item) => {
                                    return rangeObj.metric.includes('price') ?
                                        String(item.value) : String(item.label)
                                }}
                                renderItem={(item, isHighlighted) =>
                                    <div
                                    className={`query-autocomplete ${isHighlighted ? 'highlight' : ''}`}
                                    >
                                      {item.label}
                                    </div>
                                }
                                value={((value) => {return{
                                    label: rangeObj.metric.includes('price') && !!rangeObj.value
                                        ? numberWithCommas(value)
                                        : value,
                                    value: rangeObj[minMax]
                                }})(minMax === 'min' ? rangeObj.min : rangeObj.max)}
                                onFocus={() => {
                                    // TRACK: opens a filter drawer (label: which drawer)
                                    trackClick('search_form_click',`${rangeObj.metric}-${minMax}`);
                                }}
                                onChange={option => {
                                    // TRACK: changes a filter value (label: which filter, what value)
                                    trackClick('search_form_click', `${rangeObj.metric}-${minMax}: ${option.value}`);
                                    rangeObj[minMax] = option.value;
                                    return updateParams({[rangeObj.metric]: rangeObj});
                                }}
                            />
                        </div>
                    )
                })}
            </>
        )}
}
const getRangeObj = (min, max, display, metric) => {
    return {
        min: min || "",
        max: max || "",
        display: display || "",
        displayName: display || "",
        metric: metric
    }
};

class SearchForm extends React.Component {
    constructor (props) {
        super(props);
        this.defaultConfig = defaults[this.props.affiliate.split("_")[1]];
        this.emptySearchText = this.defaultConfig.emptySearchText;
        const {getUrlParams, page, maxResults} = this.props;
        const urlParams = getUrlParams();

        let defaultAddress = urlParams.address && urlParams.address.includes('_')
            ? parseCityState(urlParams.address)
            : urlParams.address && isZip(urlParams.address)
                ? urlParams.address
                : '';
        let defaultAddressDistance = urlParams.address_distance ? urlParams.address_distance : "10";
        let defaultShowRadius = urlParams.show_radius && urlParams.show_radius === "false"
                            ? false
                            : urlParams.address === this.emptySearchText
                                ? false
                                : true;
        let defaultFeatures = urlParams.features ? parseFeatures(urlParams.features) : [];
        let defaultPropertyTypes = urlParams.property_types ? parseFeatures(urlParams.property_types) : [];
        const sortOptions = getSortOptions(urlParams.list_type);
        const sortOptionsDisplay = SORT_OPTION_Display;
        this.state = {
            listType: urlParams.list_type || "resale",
            saved: urlParams.saved || 'search',
            address: defaultAddress,
            price: getRangeObj(...parseRange(urlParams.price), ...["Price", "price"]),
            beds: getRangeObj(...parseRange(urlParams.beds), ...["Beds", "beds"]),
            baths: getRangeObj(...parseRange(urlParams.baths), ...["Baths", "baths"]),
            acres: getRangeObj(...parseRange(urlParams.acres), ...["Lot Size", "acres"]),
            sqft: getRangeObj(...parseRange(urlParams.sqft), ...["SQFeet", "sqft"]),
            queryTimeout: null,
            locationDisplay: getLocationDisplay(defaultAddress, defaultAddressDistance, defaultShowRadius, this.emptySearchText),
            address_distance: urlParams.address_distance ? urlParams.address_distance : "10",
            showcase: urlParams.showcase || false,
            showRadius: urlParams.show_radius && urlParams.show_radius === "false"
                            ? false
                            : urlParams.address === this.emptySearchText
                                ? false
                                : true,
            detailItemKey: urlParams.detail_page ? urlParams.detail_page : null,
            features: {metric: "features", selected: defaultFeatures, display: "Features"},
            properties: {
                metric: "properties",
                selected: defaultPropertyTypes,
                display: propTypeString},
            sortOptions: sortOptions,
            sortOptionsDisplay: sortOptionsDisplay,
            featureList: (urlParams.list_type === 'rentals') ? RENT_FEATURES : RESALE_FEATURES,
            priceRange: (urlParams.list_type === 'rentals') ? RENTS : PRICES,
            mapSearchToggle: false,
            page: page,
            maxResults: maxResults,
            sortby: urlParams.showcase
                    ? "showcase"
                    : urlParams.sortby
                        ? (sortOptions.indexOf(urlParams.sortby) > -1)
                            ? urlParams.sortby : "price:d"
                        : "price:d",
        };
        // Address state variables that are conditioned on listType: Features and Price.
        // For these, we maintain two sets of state and update the applicable one from the URL.
        this.setFeatures({metric: "features", selected: defaultFeatures, display: "Features"})
        this.setFeatures({metric: "features", selected: [], display: "Features"}, true); // set shadow features
        if (this.state.listType === "rentals") {
            this.state.priceRent = getRangeObj(...parseRange(urlParams.price), ...["Price", "price"]);
            this.state.priceSale = this.state.priceSale || getRangeObj(null, null, "Price", "price");
        } else {
            this.state.priceSale = getRangeObj(...parseRange(urlParams.price), ...["Price", "price"]);
            this.state.priceRent = this.state.priceRent || getRangeObj(null, null, "Price", "price");
        }
    }
    getFeatures = () => {
        return this.state.listType === "rentals"
            ? this.state.featuresRental
            : this.state.featuresSale
    }
    setFeatures = (ob, shadow) => {
        // if "shadow", then set the *unseen* features for the other property type.
        // This allows us to seed the
        let setRentals = this.state.listType === "rentals";
        setRentals = shadow ? !setRentals : setRentals;
        if (setRentals) {
            this.state.featuresRental = ob;
        } else {
            this.state.featuresSale = ob;
        }
    }
    componentDidMount = () => {
        this.sendQuery();
    }
    getSnapshotBeforeUpdate() {
        return this.props || {};
    }
    componentDidUpdate(prevProps, prevState, snapshot) {
        if ((snapshot.stateUrlParams !== prevProps.stateUrlParams) && snapshot.changed) {
            let {price, beds, baths, features, property_types, acres, sqft, address, address_distance, show_radius,
                list_type, sortby, saved} = snapshot.stateUrlParams;

            // Update our location - look for valid city state, then zip, then ad number, last empty search text
            let snapshotAddress = address.includes('+') || address.includes('_')
                ? parseCityState(address)
                : !!address
                    ? address
                    : this.emptySearchText;
            // If address has adid: or is equal to emptySearchText, it is not a geo search
            this.updateLocation(snapshotAddress,
                address_distance || '10',
                show_radius && show_radius === "false" ? false : true,
                !(snapshotAddress.includes('adid:')) || !(snapshotAddress === this.emptySearchText)
            );

            [getRangeObj(...parseRange(price), ...["", 'price']),
             getRangeObj(...parseRange(beds), ...["", 'beds']),
             getRangeObj(...parseRange(baths), ...["", 'baths']),
             getRangeObj(...parseRange(acres), ...["", 'acres']),
             getRangeObj(...parseRange(sqft), ...["", 'sqft'])]
                .forEach(item => this.updateRangeDisplay(item, list_type));

            // Features objects
            const getFeaturesStateObj = (selected) => {
                return {metric: "features", selected, display: "Features"};
            };
            let featuresStateObj = getFeaturesStateObj(features ? parseFeatures(features) : []);
            let noFeaturesStateObj = getFeaturesStateObj([]);

            // Set the rest
            this.setState({
                features: featuresStateObj,
                featuresSale: (list_type === 'resale' && featuresStateObj) || noFeaturesStateObj,
                featuresRental: (list_type === 'rentals' && featuresStateObj) || noFeaturesStateObj,
                featureList: (list_type === 'rentals') ? RENT_FEATURES : RESALE_FEATURES,
                properties: {metric: "properties", selected: property_types ? parseFeatures(property_types) : [], display: propTypeString},
                priceRange: (list_type === 'rentals') ? RENTS : PRICES,
                showRadius: show_radius && show_radius === "false" ? false : true,
                listType: list_type === 'open_house' ? 'resale' : list_type,
                sortby,
                saved
                });
        }
    }
    updateQuery = (msec=1500) => {
        // call this when any input is updated. Wait <msec> milliseconds before updating
        this.props.searchUI().start();
        clearTimeout(this.queryTimeout);
        this.queryTimeout = setTimeout(() => this.sendQuery(), msec);
    }

    handleSubmit = event => {
        event.preventDefault();
        this.sendQuery();
    };
    // called when we update the search criteria
    setStateAndSend = (update, delay=false) => {
        this.setState(update, () => {
            let {priceRent, priceSale, beds, baths, acres, sqft, showcase, sortby, saved, listType} = this.state;
            const price = listType === "rentals" ? priceRent : priceSale;
            [price, beds, baths, acres, sqft].map(item => this.updateRangeDisplay(item, listType));
            // this is where we disable showcase when we UPDATE the search criteria
            this.setState({
                sortby: showcase ? "price:d" : sortby,
                showcase: false,
                saved: saved || false
            });
            return delay
                ? this.updateQuery()
                : this.updateQuery(750);
        });
    };
    sendQuery = () => {
        this.props.searchUI().start()
        this.getGeoParams().then((geo) => {
            const {address, showRadius, address_distance, acres, priceRent, priceSale, beds, baths, sqft, showcase,
                   detailItemKey, sortby, saved, maxResults, page, featuresSale, featuresRental} = this.state;
            const list_type = this.state.listType;
            const price = list_type === "rentals" ? priceRent : priceSale;
            const ad_number = (address && !geo) ? address : null;
            const mapSearch = this.state.mapSearchToggle;
            const acres_mm = this.getGteLteParams(acres.min, acres.max);
            const sqfeet_mm = this.getGteLteParams(sqft.min, sqft.max);
            const price_mm = this.getGteLteParams(price.min, price.max);
            const beds_mm = this.getGteLteParams(beds.min, beds.max);
            const baths_mm = this.getGteLteParams(baths.min, baths.max);
            const features = this.getFeatures().selected;
            const properties = this.state.properties.selected;
            const all_properties = this.state.properties;
            const urlString = getUrlString(this.state, this.emptySearchText, ad_number);
            // this goes to App.js to get AJAX results from the ES index, but we call generic_query.js to compose the request
            this.props.sendQuery(
                {geo, price_mm, beds_mm, baths_mm, features, properties, acres_mm, sqfeet_mm, maxResults, address,
                 showRadius, address_distance, ad_number, showcase, detailItemKey, mapSearch, urlString, sortby, page,
                 list_type, saved},
                ()=>{this.props.searchUI().complete()}
            );
            [price, beds, baths, acres, sqft].map(item => this.updateRangeDisplay(item, list_type));
            //send updated tags to GTM and refresh ad unit
            if (window.AdManager) {
                const GTMTags = getGTMTags(urlString);
                // console.log("GTMTags", GTMTags)
                window.AdManager.updateGAMPageTargeting(GTMTags);
                window.AdManager.refreshUnit("Window_Bottom");
            }
            this.updateListType(list_type);
            this.updateLocation(ad_number ? `adid:${address}` : address, address_distance, this.state.showRadius, geo);
            this.setState({
                properties: all_properties,
                featuresSale: featuresSale,
                featuresRental: featuresRental,
                listType: list_type
            });
        });
    };
    updateLocation = (address, address_distance, showRadius, geo) => {
        const locationDisplay = this.state.mapSearchToggle
            ? "Searching map area"
            : address
                ? getLocationDisplay(address, address_distance, showRadius, this.emptySearchText, geo)
                : this.emptySearchText;
        address = address === this.emptySearchText ? '' : address;
        this.setState({address, locationDisplay});
    };
    updateListType = (listType) => {
        this.setState({
            featureList: (listType === 'rentals') ? RENT_FEATURES : RESALE_FEATURES,
            priceRange: (listType === 'rentals') ? RENTS : PRICES,
        })
    }
    updateRangeDisplay = (rangeObj, listType) => {
        const minParam = rangeObj.metric === 'price' ? shortDollarFormat(rangeObj.min) : rangeObj.min;
        const maxParam = rangeObj.metric === 'price' ? shortDollarFormat(rangeObj.max) : rangeObj.max;
        const displayStrings = {
            price: {
                unit: "",
                default: listType === "rentals" ? "Rent" : "Price"
            },
            beds: {
                unit: " bd",
                default: "Beds"
            },
            baths: {
                unit: " ba",
                default: "Baths"
            },
            acres: {
                unit: " acre",
                default: "Lot Size"
            },
            sqft: {
                unit: "sf",
                default: "Square Feet"
            }
        }[rangeObj.metric];
        const unit = displayStrings.unit;
        const prefix = rangeObj.metric === 'price' ? '$' : '';

        const displayFormat = {
            equal: `${minParam}${unit}`,
            notEqual: `${prefix}${minParam}-${maxParam}${unit}`,
            justMin: `${prefix}${minParam}${unit}+`,
            upTo: `up to ${prefix}${maxParam}${unit}`,
            default: displayStrings.default
        };
        const display =
            minParam && minParam !== "" && maxParam && maxParam !== ""
                ? (minParam === maxParam ? displayFormat.equal : displayFormat.notEqual)
                : minParam && minParam !== ""
                    ? displayFormat.justMin
                    :   maxParam && maxParam !== ""
                        ? displayFormat.upTo
                        : displayFormat.default;
        rangeObj.display = display;

        const nullPriceObj = getRangeObj(null, null, "Price", "price");
        const priceObj = rangeObj.metric === "price"
            ? listType === "rentals"
                ? {priceSale: this.state.priceSale || nullPriceObj, priceRent: rangeObj}
                : {priceSale: rangeObj, priceRent: this.state.priceRent || nullPriceObj}
            : {};
        this.setState(() => ({[rangeObj.metric]: rangeObj, ...priceObj}))
    };

    isFilterSet = (filter) => {
        if (["price", "beds", "baths", "acres", "sqft"].indexOf(filter) > -1) {
            return !!this.state[filter].min || !!this.state[filter].max ? "set" : "unset";
        } else if (["features", "properties"].indexOf(filter) > -1){
            return this.state[filter].selected.filter((item) => item).length ? "set" : "unset";
        } else if (filter === "address") {
            return !!this.state.address ? "set" : "unset";
        }
    }

    getGeoParams = () => {
        let {address, address_distance, showRadius} = this.state;
        address_distance = showRadius ? address_distance : "0";
        let zip = /^(\d{5})$/.exec(address);
        let lat = 99.05042;
        let lon = -99.00;
        zip = zip ? zip[1] : null;
        let city_state = /(.*?), (.*)/.exec(address);
        let county_state = /(.*County?), (.*)/.exec(address);
        city_state = !city_state || county_state ? null : {city: city_state[1], state: city_state[2]};
        county_state = !county_state ? null : {county: county_state[1], state: county_state[2]};

        return new Promise((resolve) => {
            if ((zip || city_state || county_state) && address_distance !== "0") {
                let geocodeURL = 'https://geocode.search.hereapi.com/v1/geocode?apiKey=kV6FNpieIWGGmKSaFuoVQw2SAXAHQrOKmYjnYn_DEZI&limit=1&in=countryCode:USA,CAN';
                if (city_state) {
                    geocodeURL += '&qq=city=' + city_state.city + ';state=' + city_state.state;
                }
                else if (county_state) {
                    geocodeURL += '&qq=county=' + county_state.county+ ';state=' + county_state.state;
                } else
                    geocodeURL += '&q=' + zip;
                fetch(geocodeURL)
                    .then((response) => {
                        return response.json();
                    }).then((myJson) => {
                            //console.log(JSON.stringify(myJson));
                            let location = myJson.items ? myJson.items[0] : '';
                            if (location) {
                                if (location.resultType === 'country' || location.MatchLevel === 'state') {
                                    alert('geocoding error')
                                } else {
                                    zip = location.address.postalCode;
                                    lat = location.position.lat;
                                    lon = location.position.lng;
                                    const lat_lon = {"lat": lat, "lon": lon};
                                    const distance = address_distance;
                                    const ll_dist = !distance ? null : Object.assign({distance}, lat_lon);
                                    resolve(zip || city_state || county_state || ll_dist ? {zip, city_state, county_state, ll_dist} : null);
                                }
                            }  else {
                                resolve(zip || city_state || county_state ? {zip, city_state, county_state} : null)
                            }
                    });
            } else {
                resolve(zip || city_state || county_state ? {zip, city_state, county_state} : null)
            }
        })
    };
    getGteLteParams = (minParam, maxParam) => {
        let retVal = {};
        if (minParam) {
            retVal.gte = minParam;
        }
        if (maxParam) {
            retVal.lte = maxParam;
        }
        return minParam || maxParam ? retVal : null;
    };
    rangeItems = (rangeParam) => {
        return {
            priceMin: this.state.priceRange.filter((item) => (this.state.price.max ? item < Number(this.state.price.max) : true)),
            priceMax: this.state.priceRange.filter((item) => (this.state.price.min ? item > Number(this.state.price.min) : true)),
            bedsBaths: [0,1,2,3,4,5],
            acreage: [0,1,2,3,5,10],
            squareFeet: SQUAREFEET
        }[rangeParam]
    };
    render() {
        const { address, featureList, mapSearchToggle } = this.state;
        const combineDims = window.innerWidth <= 800;
        return (
          <div className="search-controls">
            { this.state.saved !== "search" &&
                <div className="disable-search"></div> }
            <form onSubmit={this.handleSubmit}>
                <Select
                    className="list-type re-select"
                    classNamePrefix="sel"
                    name="list-type"
                    placeholder="For sale"
                    isSearchable={false}
                    options={LIST_TYPES}
                    value={((value) => {
                            return value
                                ? {
                                    label: LIST_TYPE_MAP[value],
                                    value: value
                                }
                                : "LIST TYPE";
                        })(this.state.listType)}
                    onFocus={option => {
                            trackClick('search_form_click', `filterOpen: list_type`);
                        }}
                    onChange={option => {
                            trackClick('search_form_click', `list_type: ${option.value}`);
                            this.setStateAndSend({listType: option.value});
                        }
                    }
                    onClick={e => e.stopPropagation()}
               />
                <Select
                    className="favorites re-select"
                    classNamePrefix="sel"
                    name="favorites"
                    placeholder="LISTYPE"
                    isSearchable={false}
                    options={FAV_TYPES}
                    value={((value) => {
                            return value
                                ? {
                                    label: favMapForButton(value),
                                    value: value
                                }
                                : "LIST TYPE";
                        })(this.state.saved)}
                    onFocus={option => {
                            trackClick('search_form_click', `filterOpen: favorites`);
                        }}
                    onChange={option => {
                            trackClick('search_form_click', `favorites: ${option.value}`);
                            return this.setStateAndSend({saved: option.value});
                        }}
                    onClick={e => e.stopPropagation()}
               />
              <div className="menu-title location">
                  { /*<input type="radio" id="show-price"/>   open on tap for mobile? */ }
                  <label className={`${this.isFilterSet("address")}`}>{this.state.locationDisplay}</label>
                    <MagnifyingGlass />
                  <div id="set-location" className="fieldset config-panel">
                    <div>
                        <Autocomplete
                            getItemValue={(item) => item.label}
                            items={availableTags[this.props.affiliate]
                                    .filter((item) => (this.state.address.length > 1 && item.toLowerCase().includes(this.state.address.toLowerCase())))
                                    .map((item) => {return{label: item}})
                            }
                            wrapperStyle={{display: 'block', position: 'relative', zIndex: '100'}}
                            renderItem={(item, isHighlighted) =>
                                <div
                                    key={Math.random()}
                                    style={isHighlighted ? Object.assign({}, selOptionStyle, focusedStyle) : selOptionStyle}
                                >
                                    {item.label}
                                </div>
                            }
                            renderInput={props => {
                                return (
                                    <div>
                                        <input {...props} />
                                        {address
                                            ? <div
                                                className='clear-button'
                                                onClick={e => {
                                                    if (this.state.address) {
                                                        // TRACK: autocomplete clear
                                                        trackClick('search_form_click', 'location-clear: clicked');
                                                        this.setState({address: ''});
                                                    }
                                                }}
                                              ><Close/></div>
                                            : ''
                                        }
                                    </div>
                                )
                            }}
                            value={address.includes('adid:') ? address.split(':')[1] : address}
                            name="address"
                            onChange={event => this.setState({address: event.target.value, mapSearchToggle: false})}
                            onSelect={val => {
                                // TRACK: changes a filter value (label: which filter, what value)
                                trackClick('search_form_click', `location-select: ${val}`);
                                this.setStateAndSend({address: val, mapSearchToggle: false}, true)
                            }}
                            // TRACK: opens a filter drawer (label: which drawer)
                            inputProps={{
                                placeholder: "Enter city, county, zip or property ID",
                                onFocus: () => { trackClick('search_form_click', `location-select: focused`)}
                            }}
                      />

                      </div>
                  <div className={`radius-input ${this.state.showRadius ? "" : "hidden"}`}>
                    <div>
                      <label className="distance">Distance: </label>
                        <input
                            id="distance"
                            type="number"
                            name="range"
                            min="0"
                            value={this.state.address_distance}
                            onChange={event => {
                                event.preventDefault();
                                if (event.target.value) {
                                    // TRACK: changes a filter value (label: which filter, what value)
                                    trackClick('search_form_click', `distance: ${event.target.value}`);
                                    this.setStateAndSend({address_distance: event.target.value, mapSearchToggle: false}, true);
                                }
                            }}
                            onFocus={ () => { trackClick('search_form_click', 'distance-input: focused')}}
                        /> <span className="mi">miles</span>
                    </div>
                  </div>
                      <div className="no-radius">
                      <button onClick={(e) => {
                          e.preventDefault();
                          if (e.detail) {
                              // TRACK: changes a filter value (label: which filter, what value)
                              trackClick('search_form_click', `show-radius: ${!this.state.showRadius}`);
                              this.setStateAndSend({showRadius: !this.state.showRadius, mapSearchToggle: false});
                          } else {
                              this.setStateAndSend({mapSearchToggle: false});
                          }
                      }}>
                          {this.state.showRadius ? "Use exact" : "Add radius"}
                      </button>
                      </div>
                    </div>
              </div>
                {(() => {
                return <SearchFilter
                    filterObj={this.state.listType === "rentals" ? this.state.priceRent : this.state.priceSale}
                    setFilterState={this.setStateAndSend}>
                    <RangeSearch
                        minLabel="Min"
                        maxLabel="Max"
                        rangeObj={this.state.listType === "rentals" ? this.state.priceRent : this.state.priceSale}
                        minRange={this.rangeItems('priceMin')}
                        maxRange={this.rangeItems('priceMax')}
                        step="100000"
                        updateParams={this.setStateAndSend}
                    />
                </SearchFilter>
                })()
                }
              <FilterGroup
                combineDims={combineDims}
                filterObjs={[this.state.beds, this.state.baths, this.state.sqft, this.state.acres]}
              >
                <SearchFilter filterObj={this.state.beds} setFilterState={this.setStateAndSend}
                    headless={combineDims}>
                    <RangeSearch
                        minLabel="Min"
                        maxLabel="Max"
                        rangeObj={this.state.beds}
                        minRange={this.rangeItems('bedsBaths')}
                        maxRange={this.rangeItems('bedsBaths')}
                        step=".5"
                        updateParams={this.setStateAndSend}
                    />
                </SearchFilter>
                <SearchFilter filterObj={this.state.baths} setFilterState={this.setStateAndSend}
                    headless={combineDims}>
                    <RangeSearch
                        minLabel="Min"
                        maxLabel="Max"
                        rangeObj={this.state.baths}
                        minRange={this.rangeItems('bedsBaths')}
                        maxRange={this.rangeItems('bedsBaths')}
                        step=".5"
                        updateParams={this.setStateAndSend}
                    />
                </SearchFilter>
                <SearchFilter filterObj={this.state.sqft} setFilterState={this.setStateAndSend}
                    headless={combineDims}>
                    <RangeSearch
                        minLabel="Min"
                        maxLabel="Max"
                        rangeObj={this.state.sqft}
                        minRange={this.rangeItems('squareFeet')}
                        maxRange={this.rangeItems('squareFeet')}
                        step="200"
                        updateParams={this.setStateAndSend}
                    />
                </SearchFilter>
                <SearchFilter filterObj={this.state.acres} setFilterState={this.setStateAndSend}
                    headless={combineDims}>
                    <RangeSearch
                        minLabel="Min"
                        maxLabel="Max"
                        rangeObj={this.state.acres}
                        minRange={this.rangeItems('acreage')}
                        maxRange={this.rangeItems('acreage')}
                        step="1"
                        updateParams={this.setStateAndSend}
                    />
                </SearchFilter>
                </FilterGroup>
                <SearchFilter filterObj={this.getFeatures()} setFilterState={this.setStateAndSend} listType={this.state.listType}>
                        <div>
                            <ul className="ul-list">
                                {featureList.map((feature, index) => {
                                    const featuresState = this.getFeatures();
                                    if (featuresState === undefined) {
                                        console.log('no features');
                                    }
                                    const features = new Set(featuresState.selected);
                                    const stateKey = this.state.listType === "rentals" ? "featuresRental" : "featuresSale";
                                    // *virtually* select Open House if the list-type is open house.
                                    const selectForOpenHouse = feature.name === "open_house" && this.state.listType === "open_house";
                                    return (
                                        <li key={index}>
                                            <input className="featuresCheckbox"
                                                   type="checkbox"
                                                   name={feature.name}
                                                   checked={selectForOpenHouse || features.has(feature.name)}
                                                   disabled={selectForOpenHouse}
                                                   onChange={event => {
                                                       if (event.target.checked) {
                                                        // TRACK: changes a filter value (label: which filter, what value)
                                                        trackClick('search_form_click', `features: ${feature.name}`);
                                                        features.add(feature.name)
                                                       } else {
                                                        features.delete(feature.name)
                                                       }
                                                       this.setStateAndSend({[stateKey]: {
                                                        metric: "features",
                                                        selected: Array.from(features),
                                                        display: "Features"
                                                        }});
                                                   }}
                                                   onClick={e => e.stopPropagation()}
                                            />
                                            <label>{feature.label}</label>
                                            <br/><br/>
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                </SearchFilter>
                <SearchFilter filterObj={this.state.properties} setFilterState={this.setStateAndSend}>
                        <div>
                            <ul className="ul-list">
                                {PROPERTY_TYPES.map((property, index) => {
                                    let properties = new Set(this.state.properties.selected);
                                    return (
                                        <li key={index}>
                                            <input className="featuresCheckbox"
                                                   type="checkbox"
                                                   name={property}
                                                   checked={properties.has(property)}
                                                   onChange={event => {
                                                       if (event.target.checked) {
                                                        // TRACK: changes a filter value (label: which filter, what value)
                                                        trackClick('search_form_click', `properties: ${property}`);
                                                        properties.add(property)
                                                       } else {
                                                        properties.delete(property)
                                                       }
                                                       this.setStateAndSend({properties: {
                                                        metric: "properties",
                                                        selected: Array.from(properties),
                                                        display: propTypeString
                                                        }})
                                                   }}
                                            />
                                            <label>{property}</label>
                                            <br/><br/>
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    </SearchFilter>
                    {/* <div className="sortContainer">
                        <div id="sortLabel">Sort:</div> */}
                        <Select
                            className="sort re-select asset_div"
                            classNamePrefix="sel"
                            name="sortby"
                            placeholder="Sort"
                            isSearchable={false}
                            options={this.state.sortOptions}
                            value={((value) => {
                                    return value
                                    ? {label: (this.state.sortOptionsDisplay)[value], value: value}
                                    : "Sort";
                                })(this.state.sortby)}
                            onChange={option => {
                                    // TRACK: changes a filter value (label: which filter, what value)
                                    trackClick('search_form_click', `sortby: ${option.value}`);
                                    return this.setStateAndSend({sortby: option.value});
                                }}
                            onFocus={option => {
                                    // TRACK: changes a filter value (label: which filter, what value)
                                    trackClick('search_form_click', `filterOpen: sortby`);
                                }}
                        />
                 {/* </div> */}
                <Toggle id="map-search-toggle"
                    onText="Search current map area"
                    offText="Search current map area"
                    defaultStatus={mapSearchToggle}
                    className={`map-search-toggle${this.props.defaultMapView ? '' : ' hidden'}`}
                    callback={(newState) => {
                            //console.log("NEW MAP SEARCH STATE" + newState);
                            this.setStateAndSend({mapSearchToggle: newState})
                        }}
                    checkbox="true"
                />
            </form>
          </div>
        );
    }
}

const isFilterSet = (filter) => {
        const metric = filter.metric;
        if (["price", "beds", "baths", "acres", "sqft"].indexOf(metric) > -1) {
            return !!filter.min || !!filter.max ? "set" : "unset";
        } else if (["properties", "features"].indexOf(metric) > -1) {
            return filter.selected.filter((item) => item).length ? "set" : "unset";
        } /* Migrate address later:
            else if (feature === "address") {
            return !!this.state.address ? "set" : "unset";
        }*/
    }


class SearchFilter extends React.Component {
    unsetFilter = (filter, listType = 'resale') => {
        const metric = filter.metric;
        // TRACK: clear Filter
        trackClick('search_form_click', `clear-filter: ${metric}`);
        if (["price", "beds", "baths", "acres", "sqft"].indexOf(metric) > -1) {
            const metricObject = filter;
            metricObject.min = null;
            metricObject.max = null;
            this.props.setFilterState({
                [metric]: metricObject
            })
        } else if (["properties", "features"].indexOf(metric) > -1) {
            this.props.setFilterState({
                [metric === "features"
                    ? listType === "resale"
                        ? "featuresSale" : "featuresRental"
                    : metric]: {
                    metric: metric,
                    display: filter.display,
                    selected: []
                }
            })
        }
    }
    render() {
        const {filterObj, listType, children, headless = false} = this.props;
        return (
            !headless
            ? (
                <div className={`menu-title ${filterObj.metric}`}>
                        <label className={isFilterSet(filterObj)}>{filterObj.display}</label>
                        <DropdownCaret />
                  <div className="fieldset config-panel">
                    <div className="config-group">
                        {children}
                        <a className="clear-filter" onClick={()=>this.unsetFilter(filterObj, listType)}>Clear</a>
                    </div>
                  </div>
                </div> )
            : (
                <div className="fieldset config-group">
                    <div className="group-name">{filterObj.displayName}</div>
                    {children}
                    <a className="clear-filter" onClick={()=>this.unsetFilter(filterObj, listType)}>Clear</a>
                </div>
            )
        )
    }
}

class FilterGroup extends React.Component {
    render() {
        const {children, filterObjs, combineDims} = this.props;
        const isSet = filterObjs.some(obj => isFilterSet(obj) === "set");
        const reduceString = x => (
            x.length < 18
            ? "Dims: " + x
            : x.length > 23
                ? x.substring(0,21).replace(/\s+\S*$/, "") + "..."
                : x);

        return !combineDims
            ? children
            : (
                <div className={`menu-title dimensions`}>
                    <label className={isSet ? "set" : "unset"}>
                        {!isSet
                         ? "Rooms & Dimensions"
                         : reduceString(filterObjs.filter(obj => isFilterSet(obj) === 'set').map(obj => obj.display).join(" "))}
                    </label>
                    <DropdownCaret />
                    <div className="config-panel fieldset">
                        {children}
                    </div>
                </div>


            )
    }
}

export {SearchForm}
